import { FileDown } from 'lucide-react';
import { Card } from '@siliconx/shared/ui/Card';
import { SectionLabel } from '@siliconx/shared/ui/SectionLabel';
import { StatusPill, type PillTone } from '@siliconx/shared/ui/StatusPill';
import { ESG_LOCATION_BASED_METHOD_BADGE, ESG_LOCATION_BASED_METHOD_NOTE } from '@siliconx/shared';
import type { EnvironmentalVerificationStatus } from '@siliconx/shared';
import { apiDownload } from '../../lib/api';
import { METHODOLOGY_CARD_INTRO } from './reportsCopy';

/** Rung -> pill tone. Rung 0 is deliberately NEUTRAL, never green — a green
 * chip on an unverified claim would read as a false all-clear (SIL-459 R5).
 * Rungs 1/2 are additive, unreached today. */
const RUNG_TONE: Record<0 | 1 | 2, PillTone> = { 0: 'neutral', 1: 'amber', 2: 'green' };

/**
 * SIL-477 — Reports tab, SIL-459 register R5/R6. Replaces the design mock's
 * banned "Assurance" card (no "Audit-ready" badge, no "Third-party assured"
 * pill, no ISAE reference) with: the Protocol-consistent framing line, the
 * SIL-426 §2 location-based method chip (reused verbatim, never retyped),
 * a methodology-sheet download, and an upgradeable status slot that renders
 * WHATEVER the server sends — never a hardcoded UI string.
 */
export function MethodologyVerificationCard({
  verification,
}: {
  verification: EnvironmentalVerificationStatus | undefined;
}) {
  return (
    <Card padding="md" className="flex flex-col gap-3">
      <SectionLabel>Methodology & verification</SectionLabel>
      <p className="text-xs leading-relaxed text-zinc-600">{METHODOLOGY_CARD_INTRO}</p>

      <div className="flex items-center gap-2">
        <span
          title={ESG_LOCATION_BASED_METHOD_NOTE}
          className="inline-flex cursor-default items-center rounded border border-zinc-300 bg-white px-1.5 py-0.5 text-xs font-semibold uppercase tracking-wider text-zinc-600"
          data-testid="reports-location-based-method-chip"
        >
          {ESG_LOCATION_BASED_METHOD_BADGE}
        </span>
      </div>

      <button
        type="button"
        onClick={() => void apiDownload('/api/client/environmental/methodology.csv', 'environmental-methodology.csv')}
        className="inline-flex w-fit cursor-pointer items-center gap-1.5 text-xs font-medium text-brand-700 hover:underline"
        data-testid="methodology-sheet-link"
      >
        <FileDown className="size-3.5" />
        Methodology sheet (CSV)
      </button>

      <div className="mt-1 border-t border-zinc-200 pt-3">
        <div className="mb-1.5 flex items-center gap-2">
          <span className="text-xs font-semibold uppercase tracking-widest text-zinc-500">
            Verification status
          </span>
          {verification && (
            <span data-testid="verification-status-pill">
              <StatusPill tone={RUNG_TONE[verification.rung]}>{verification.label}</StatusPill>
            </span>
          )}
        </div>
        {verification && <p className="text-xs leading-relaxed text-zinc-600">{verification.statement}</p>}
      </div>
    </Card>
  );
}
